import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle, 
  TextField,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Restore as RestoreIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useLead } from '../../contexts/LeadContext';
import { useModal } from '../../contexts/ModalContext';

const LeadRestore = ({
  open,
  onClose,
  leadIds = [],
  leadName = '',
  leadId = '',
  leadDetails = [], // Array of lead objects with id, name, email, etc.
  onComplete = () => {},
  onSuccess = () => {},
  allRestoredLeads = false
}) => {
  console.log('lead ids', leadIds);
  const { restoreLead } = useLead();
  const { openSuccessModal, openErrorModal } = useModal();

  const LEAD_RESTORE_REASONS = [
    "Lead deleted by mistake",
    "Customer contacted again",
    "Requirements changed",
    "Budget situation improved",
    "Timing is now right",
    "Found suitable property",
    "Personal circumstances changed",
    "Financing approved",
    "Customer reconsidered",
    "Other"
  ];

  const [selectedReason, setSelectedReason] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [restoreResults, setRestoreResults] = useState({ passed: 0, errored: 0, results: [] });
  const { enqueueSnackbar } = useSnackbar();
  const [error, setError] = useState(null);

  const handleReasonChange = (event, newValue) => {
    setSelectedReason(newValue);
  };

  const handleAdditionalNotesChange = (e) => {
    setAdditionalNotes(e.target.value);
  };

  const handleRestore = async () => {
    if (!selectedReason) {
      enqueueSnackbar('Please select a reason for restoring', { variant: 'error' });
      return;
    }

    // Combine the selected reason and additional notes
    const finalNotes = additionalNotes 
      ? `${selectedReason} - ${additionalNotes}`
      : selectedReason;

    // Get the correct lead IDs
    const ids = allRestoredLeads ? leadIds : leadId ? [leadId] : leadIds;
    console.log('ids', ids);
    
    if (!ids.length) {
      enqueueSnackbar('No leads selected for restoration', { variant: 'error' });
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const payload = {
        lead_ids: ids,
        notes: finalNotes,
        all_restored: allRestoredLeads ? true : false
      };

      const response = await restoreLead(payload);
      
      if (response.status === 200) {
        setRestoreResults(response.data);
        setShowResults(true);
        if (response.data.passed > 0) {
          onSuccess();
        }

        if (response.data.passed > 0) {
          enqueueSnackbar(
            `Successfully restored ${response.data.passed} lead${response.data.passed > 1 ? 's' : ''}`, 
            { variant: 'success' }
          );
        }
        
        if (response.data.errored > 0) {
          enqueueSnackbar(
            `Failed to restore ${response.data.errored} lead${response.data.errored > 1 ? 's' : ''}`, 
            { variant: 'warning' }
          );
        }
      } else {
        setError(response.data?.message || 'Failed to restore leads');
        enqueueSnackbar('Failed to restore leads', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error restoring leads:', error);
      setError('An unexpected error occurred while restoring leads');
      enqueueSnackbar('An unexpected error occurred', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseResults = () => {
    setShowResults(false);
    setRestoreResults({ passed: 0, errored: 0, results: [] });
    setSelectedReason('');
    setAdditionalNotes('');
    setError(null);
    onClose();
    onComplete();
  };

  const handleClose = () => {
    if (isSubmitting) return;
    
    setSelectedReason('');
    setAdditionalNotes('');
    setError(null);
    setShowResults(false);
    setRestoreResults({ passed: 0, errored: 0, results: [] });
    onClose();
  };

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedReason('');
      setAdditionalNotes('');
      setError(null);
      setShowResults(false);
      setRestoreResults({ passed: 0, errored: 0, results: [] });
    }
  }, [open]);

  const isBulkMode = leadIds.length > 1 || (!leadId && leadIds.length === 0);

  if (showResults) {
    const successCount = restoreResults.passed || 0;
    const failedCount = restoreResults.errored || 0;

    return (
      <Dialog 
        open={showResults} 
        onClose={handleCloseResults}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <RestoreIcon color="success" />
            <Typography variant="h6">Restore Results</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            {/* Summary Banner */}
            <Alert 
              severity={successCount > 0 ? "success" : "error"}
              icon={successCount > 0 ? <CheckCircleIcon /> : <ErrorIcon />}
            >
              <Typography variant="body1" fontWeight="bold">
                {successCount > 0 
                  ? `Successfully restored ${successCount} lead${successCount !== 1 ? 's' : ''}`
                  : `Failed to restore ${failedCount} lead${failedCount !== 1 ? 's' : ''}`
                }
                {failedCount > 0 && successCount > 0 && `, ${failedCount} lead${failedCount !== 1 ? 's' : ''} failed`}
              </Typography>
            </Alert>

            {/* Successfully Restored Leads Section */}
            {successCount > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom color="success.main">
                  Successfully Restored Leads
                </Typography>
                <TableContainer component={Paper} sx={{ maxHeight: 300, overflow: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'success.light' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Lead ID</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Contact</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Result</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {restoreResults.results
                        .filter(lead => lead.message?.includes('successfully'))
                        .map((lead, index) => {
                          return (
                            <TableRow key={lead.lead_id} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'text.gray' } }}>
                              <TableCell sx={{ fontWeight: 'bold' }}>#{lead.lead_id}</TableCell>
                              <TableCell>{lead.name}</TableCell>
                              <TableCell>{lead.contact}</TableCell>
                              <TableCell sx={{ color: 'success.main' }}>{lead.message}</TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Failed Restorations Section */}
            {failedCount > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom color="error.main">
                  Failed to Restore
                </Typography>
                <TableContainer component={Paper} sx={{ maxHeight: 300, overflow: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'error.light' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Lead ID</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Contact</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Result</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {restoreResults.results
                        .filter(lead => !lead.message?.includes('successfully'))
                        .map((lead, index) => {
                          return (
                            <TableRow key={lead.lead_id} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'error.light' } }}>
                              <TableCell sx={{ fontWeight: 'bold' }}>#{lead.lead_id}</TableCell>
                              <TableCell>{lead.name}</TableCell>
                              <TableCell>{lead.contact}</TableCell>
                              <TableCell sx={{ color: 'error.main' }}>{lead.message}</TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseResults}
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="lead-restore-dialog-title">
        {allRestoredLeads ? 'Restore All Leads' : 'Restore Lead'}
      </DialogTitle>
      <DialogContent>
        <Box>
          <Typography variant="body2">
            <span>Lead: {leadName}</span>
          </Typography>
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {allRestoredLeads
                ? 'All selected leads will be restored. They will become active again.'
                : 'This lead will be restored. It will become active again.'
              }
            </Typography>
          </Box>
        </Box>
        
        <Box mt={3}>
          <Autocomplete
            id="restore-reason"
            options={LEAD_RESTORE_REASONS}
            fullWidth
            value={selectedReason}
            onChange={handleReasonChange}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Reason for Restoring *" 
                variant="outlined"
                error={!selectedReason}
                helperText={!selectedReason ? "Please select a reason" : ""}
              />
            )}
          />
        </Box>
        
        <Box mt={2}>
          <TextField
            margin="dense"
            id="notes"
            label="Additional Notes"
            type="text"
            fullWidth
            multiline
            rows={3}
            value={additionalNotes}
            onChange={handleAdditionalNotesChange}
            variant="outlined"
            placeholder="Add any additional details here..."
          />
        </Box>
        
        {error && (
          <Box mt={2}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose} 
          color="primary"
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleRestore} 
          color="success" 
          variant="contained"
          disabled={isSubmitting || !selectedReason}
        >
          {isSubmitting
            ? isBulkMode ? 'Restoring Leads...' : 'Restoring...'
            : isBulkMode ? 'Restore Leads' : 'Restore Lead'
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeadRestore; 